import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { showModal } from "@/redux/message/messageSlice";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { APIStatus } from "../../../../api/APIStatus.type";
import {
  closingCross,
  loadIcon,
  saveIcon,
  shareIcon,
} from "../../../../designSystemIcons";
import { useSticky } from "../../../../hooks/useSticky";
import { PlayerRole } from "../../../../redux/players/playersSlice";
import {
  PlayerFilter,
  RightPanelMode,
  Squad,
  SquadBuilderMode,
  SquadBuilderPlayerType,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { Tactic } from "../../../../redux/tactics/tacticsSlice";
import { LoadingModal } from "../../../molecules/Modals/SpecificModals/LoadingModal";
import { PitchButton } from "../../../organisms/SquadBuilder/PitchButtons/PitchButton";
import { PitchButtons } from "../../../organisms/SquadBuilder/PitchButtons/PitchButtons";
import {
  ClickPlayerType,
  PitchLayout,
} from "../components/PitchLayout/PitchLayout";
import { SelectManager } from "../components/SelectManager/SelectManager";
import { MobileBuildSquadsButton } from "../components/SuggestSquadsButton/MobileBuildSquadsButton";

type Props = {
  squad: Squad;
  tempSquad: Squad;
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  statusBuildSquad: APIStatus;
  statusImportSquad: APIStatus;
  mode: SquadBuilderMode;
  handleChangePlayerRole: (index: number) => void;
  handleRemovePlayerOrRole: (index: number) => void;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleOnClickPlayer: ClickPlayerType;
  playerRoles: PlayerRole[];
  handleOnClickPlayerMobile?: (index: number) => void;
  handleMovePlayer?: (fromIndex: number, toIndex: number) => void;
  handleAddEvoPlayer?: (index: number) => void;
  inputFormationIndex?: number;
  tempInputFormationIndex?: number;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  playerFilters: PlayerFilter[];
  handleClearPlayerFilters?: (index: number) => void;
  handleChangeChemstyle?: (index: number) => void;
  handleSelectManager?: (id: number, type: "country" | "league") => void;
  handleCloseLoadingModal: () => void;
  handleCloseModal: () => void;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  mobileHideCenter?: boolean;
  handleClickedBuildSquads?: () => void;
  disablePlayerInteractions: boolean;
  handleOnClickPitchMobile?: () => void;
  handleClickPitchButton: (
    action: "save" | "share" | "import" | "clear",
  ) => void;
  showModals?: "save" | "share";
  chosenTactic: Tactic;
  hasChangedSquad: boolean;
  changeBuiltSquadsIndex: (index: number) => void;
  currentBuiltSquadsIndex: number;
  totalAmountOfBuiltSquads: number;
  tempFormation: string;
  hasLoadedSquad: boolean;
};

export const CenterPanel = ({
  squad,
  tempSquad,
  benchItems,
  tempBenchItems,
  statusBuildSquad,
  statusImportSquad,
  mode,
  handleChangePlayerRole,
  handleRemovePlayerOrRole,
  handleAddPlayer,
  handleAddPlayerFilters,
  handleAddEvoPlayer,
  handleMovePlayer,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  playerRoles,
  changingPitchItemOnIndex,
  rightPanelMode,
  playerFilters,
  handleClearPlayerFilters,
  handleChangeChemstyle,
  handleSelectManager,
  handleCloseLoadingModal,
  tabletOrLargerMode,
  desktopMode,
  mobileHideCenter,
  handleClickedBuildSquads,
  disablePlayerInteractions,
  handleOnClickPitchMobile,
  handleClickPitchButton,
  showModals,
  chosenTactic,
  hasChangedSquad,
  changeBuiltSquadsIndex,
  currentBuiltSquadsIndex,
  totalAmountOfBuiltSquads,
  tempFormation,
  hasLoadedSquad,
}: Props) => {
  const dispatch = useDispatch();
  const topToPage = useSelector(topToPageSelector);

  const [hasReachedRef, stickyRef] = useSticky({
    triggerOn: "bottom",
    offset: topToPage.value,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  const canClearSquad =
    squad.players.find((player) => player.resourceId) ||
    benchItems.find((player) => player.resourceId);

  const handleShareSquad = () => {
    const squadData = {
      formation: chosenTactic.teamTactics.formationId,
      players: [
        ...squad.players.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          evoPath: player.evolutionPath?.join("-"),
          evoPathId: player.evoPathId,
        })),
        ...benchItems.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          evoPath: player.evolutionPath?.join("-"),
          evoPathId: player.evoPathId,
        })),
      ],
      manager: {
        countryId: squad.manager?.country,
        leagueId: squad.manager?.league,
      },
      tacticId: "",
      tactic: {
        name: chosenTactic.name,
        description: chosenTactic.description,
        teamTactics: {
          buildUpStyle: chosenTactic.teamTactics.buildUpStyle,
          formationId: chosenTactic.teamTactics.formationId,
          defensiveApproach: chosenTactic.teamTactics.defensiveApproach,
          lineHeight: chosenTactic.teamTactics.lineHeight,
        },
        logoUrl: chosenTactic.logoUrl,
        playerRoles: chosenTactic.playerRoles.map((p) => ({
          playerRoleId: p.playerRoleId,
          focus: p.focus,
        })),
      },
    };

    dispatch(
      showModal({
        modalType: "share-squad",
        data: squadData,
      }),
    );
  };

  return (
    <section
      className={clsx(
        "flex flex-col items-center w-full touch-device:min-h-[1150px] overflow-visible",
        mobileHideCenter && "hidden",
      )}
      style={{
        background: "linear-gradient(113deg, #1C2A37 9.96%, #0F1821 86.37%)",
        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset",
      }}
      onClick={(e) => {
        if (!desktopMode) {
          handleOnClickPitchMobile && handleOnClickPitchMobile();
          e.stopPropagation();
        }
      }}
    >
      {!desktopMode && <div className="absolute top-[-44px]" ref={stickyRef} />}
      <PitchLayout
        benchItems={benchItems}
        tempBenchItems={tempBenchItems}
        handleOnClickPlayer={handleOnClickPlayer}
        handleOnClickPlayerMobile={handleOnClickPlayerMobile}
        tabletOrLargerMode={tabletOrLargerMode}
        squad={squad}
        tempSquad={tempSquad}
        statusBuildSquad={statusBuildSquad}
        mode={mode}
        handleChangePlayerRole={handleChangePlayerRole}
        handleRemovePlayerOrRole={handleRemovePlayerOrRole}
        handleAddPlayer={handleAddPlayer}
        handleAddPlayerFilters={handleAddPlayerFilters}
        changingPitchItemOnIndex={changingPitchItemOnIndex}
        rightPanelMode={rightPanelMode}
        playerRoles={playerRoles}
        handleMovePlayer={handleMovePlayer}
        handleChangeChemstyle={handleChangeChemstyle}
        handleAddEvoPlayer={handleAddEvoPlayer}
        playerFilters={playerFilters}
        handleClearPlayerFilters={handleClearPlayerFilters}
        desktopMode={desktopMode}
        disablePlayerInteractions={disablePlayerInteractions}
        formation={chosenTactic.teamTactics.formationId}
        tempFormation={tempFormation}
      />
      {!desktopMode && (
        <div
          className={clsx(
            "z-20 right-2",
            hasReachedRef ? "fixed" : "absolute top-2",
            topToPage.tailwindString,
          )}
        >
          <MobileBuildSquadsButton
            handleClickedBuildSquads={handleClickedBuildSquads}
            statusBuildSquad={statusBuildSquad}
            changeBuiltSquadsIndex={changeBuiltSquadsIndex}
            currentBuiltSquadsIndex={currentBuiltSquadsIndex}
            totalAmountOfBuiltSquads={totalAmountOfBuiltSquads}
          />
        </div>
      )}
      {desktopMode &&
        (tempSquad.manager.country ||
          squad.manager.country ||
          tempSquad.manager.league ||
          squad.manager.league ||
          mode === "default") && (
          <div className="absolute top-4 left-[280px] sb-v2-lg:left-[372px] z-30">
            <SelectManager
              country={tempSquad.manager.country || squad.manager.country}
              league={tempSquad.manager.league || squad.manager.league}
              disabled={mode === "best-formation"}
              onSelect={handleSelectManager}
            />
          </div>
        )}
      {desktopMode && (
        <div className="absolute top-4 right-[280px] sb-v2-lg:right-[372px] z-20">
          <PitchButtons>
            <PitchButton
              loading={false}
              disabled={!hasChangedSquad}
              highlighted={
                squad.players?.every((p) => p.resourceId) && hasChangedSquad
              }
              onClick={() => handleClickPitchButton("save")}
              label={hasLoadedSquad && hasChangedSquad ? "Overwrite" : "Save"}
              icon={saveIcon}
            />
            <PitchButton
              loading={false}
              disabled={false}
              onClick={handleShareSquad}
              label={"Share"}
              icon={shareIcon}
            />
            <PitchButton
              loading={statusImportSquad === "pending"}
              disabled={false}
              fixedWidth="w-[82px]"
              onClick={() =>
                canClearSquad
                  ? handleClickPitchButton("clear")
                  : handleClickPitchButton("import")
              }
              label={canClearSquad ? "Clear" : "Import Squad"}
              icon={
                canClearSquad ? (
                  <div className="flex items-center justify-center w-5 h-5 p-1 my-auto bg-gray-200 rounded-full fill-gray-875">
                    {closingCross}
                  </div>
                ) : (
                  loadIcon
                )
              }
            />
          </PitchButtons>
        </div>
      )}
      {/* Figure out what do to here*/}
      <div
        className={clsx(
          "absolute z-20 text-white -translate-x-1/2 left-1/2 top-16 touch-device:top-1/4 w-full mobile:w-auto",
          "transition-opacity duration-300 ease-out",
          statusBuildSquad === "pending" ||
            statusBuildSquad === "rejected" ||
            showModals === "save"
            ? "opacity-100" // Show modal
            : "opacity-0", // Hide modal with animation
        )}
      >
        {statusBuildSquad === "pending" || statusBuildSquad === "rejected" ? (
          <LoadingModal
            text={"Building Squads..."}
            rejected={statusBuildSquad === "rejected"}
            onClose={handleCloseLoadingModal}
          />
        ) : null}
      </div>
      {/* )} */}
    </section>
  );
};
